import type { PointerEvent, ReactElement } from 'react';
import { useId } from 'react';
import type { PhotoPolyline } from '@/entities/edit-area';
import { closedPathOf } from '../model/svg-path';

interface ShapeHoleOverlayProps {
  /** 구멍 고리(사진 px). */
  readonly ring: PhotoPolyline;
  /** 사진 위·목록에서 고른 구멍 — 실선으로 진하게 그린다. */
  readonly selected: boolean;
  /** 누를 수 있는가(부모 영역을 고른 선택 도구). 아니면 포인터 이벤트를 받지 않는다. */
  readonly interactive: boolean;
  readonly label: string;
  /** 빗금 간격(사진 px). 사진 크기에 맞춰 화면에서 비슷한 간격으로 보이게 한다. */
  readonly hatchPx: number;
  readonly testId: string;
  /** 누르기 — 고르기·끌기, 고른 구멍을 옮기지 않고 다시 누르면 점 편집(더블클릭도 같은 길로 켜진다). */
  readonly onPointerDown: (event: PointerEvent<SVGElement>) => void;
}

/**
 * 인쇄 제외 영역 한 개 — 인쇄 영역과 구분되게 점선 테두리와 옅은 빗금으로 채운다. 고르면 실선으로 진하게 그린다.
 */
export const ShapeHoleOverlay = ({
  ring,
  selected,
  interactive,
  label,
  hatchPx,
  testId,
  onPointerDown,
}: ShapeHoleOverlayProps): ReactElement => {
  // useId 의 ':' 는 url(#…) 참조를 깨뜨릴 수 있어 지운다.
  const patternId = `hole-hatch-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <g>
      <defs>
        <pattern
          id={patternId}
          width={hatchPx}
          height={hatchPx}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={hatchPx}
            stroke="#64748b"
            strokeOpacity={0.45}
            strokeWidth={hatchPx / 4}
          />
        </pattern>
      </defs>
      <path
        d={closedPathOf(ring)}
        fill={`url(#${patternId})`}
        stroke={selected ? '#1d4ed8' : '#475569'}
        strokeWidth={selected ? 3 : 1.5}
        strokeDasharray={selected ? undefined : '6 4'}
        vectorEffect="non-scaling-stroke"
        pointerEvents={interactive ? 'all' : 'none'}
        style={{ cursor: interactive ? 'move' : 'default' }}
        data-testid={testId}
        data-selected={selected ? 'true' : 'false'}
        onPointerDown={onPointerDown}
      >
        <title>{label}</title>
      </path>
    </g>
  );
};
